import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Brain, CalendarDays, CheckCircle2, Clock3, Loader2, MapPinned, Plus, Users } from 'lucide-react'

export default function BrainEmprendedorDashboard() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [createState, setCreateState] = useState({
    name: '',
    description: '',
    objective: '',
    sending: false,
    error: ''
  })
  const primaryRoom = data?.rooms?.[0] || null

  const loadDashboard = async (signal) => {
    const response = await fetch('/api/brain/dashboard', { credentials: 'include', signal })
    if (!response.ok) {
      throw new Error('No se pudo cargar el modulo BrainEmprendedor.')
    }

    return response.json()
  }

  useEffect(() => {
    let ignore = false

    const load = async () => {
      try {
        const payload = await loadDashboard()
        if (!ignore) {
          setData(payload)
        }
      } catch (err) {
        if (!ignore) {
          setError(err.message || 'No se pudo cargar el dashboard.')
        }
      } finally {
        if (!ignore) {
          setLoading(false)
        }
      }
    }

    load()
    return () => { ignore = true }
  }, [])

  const createRoom = async (event) => {
    event.preventDefault()
    setCreateState((current) => ({ ...current, sending: true, error: '' }))

    try {
      const response = await fetch('/api/brain/rooms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: createState.name,
          description: createState.description,
          objective: createState.objective,
          scheduledStartAt: null,
          startNow: true
        })
      })

      if (!response.ok) {
        throw new Error('No se pudo crear la room.')
      }

      const payload = await loadDashboard()
      setData(payload)
      setCreateState({
        name: '',
        description: '',
        objective: '',
        sending: false,
        error: ''
      })
    } catch (err) {
      setCreateState((current) => ({
        ...current,
        sending: false,
        error: err.message || 'No se pudo crear la room.'
      }))
    }
  }

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center text-slate-600">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Cargando BrainEmprendedor...
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 text-rose-700">
        {error}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] bg-slate-950 text-slate-100 overflow-hidden">
        <div className="grid lg:grid-cols-[1.5fr_1fr]">
          <div className="p-8 md:p-10">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 px-3 py-1 text-xs uppercase tracking-wide text-amber-200">
              <Brain className="w-3.5 h-3.5" />
              BrainEmprendedor LATAM
            </div>
            <h1 className="mt-5 font-display text-4xl text-white">
              Reuniones con memoria, foco y siguientes pasos.
            </h1>
            <p className="mt-4 max-w-2xl text-slate-300">
              Crea rooms privadas para founders, operaciones, producto o ventas. Cada sesion deja contexto util:
              participantes, acuerdos, tareas, preguntas a IA y feedback final.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={primaryRoom ? `/brain-emprendedor/rooms/${primaryRoom.id}` : '/brain-emprendedor/dashboard'} className="inline-flex items-center gap-2 rounded-xl bg-amber-400 px-5 py-3 font-semibold text-slate-950 hover:bg-amber-300 transition-colors">
                Abrir room activa
                <ArrowRight className="w-4 h-4" />
              </Link>
              <div className="inline-flex items-center gap-2 rounded-xl border border-slate-700 px-4 py-3 text-sm text-slate-200">
                <MapPinned className="w-4 h-4 text-amber-300" />
                Foco LATAM: equipos distribuidos, decisiones rapidas y seguimiento visible
              </div>
            </div>
          </div>

          <div className="border-t lg:border-t-0 lg:border-l border-slate-800 bg-slate-900/70 p-8">
            <p className="text-sm font-semibold text-slate-200">Estado del piloto</p>
            <div className="mt-4 space-y-4">
              {(data?.summaryCards || []).map((card) => (
                <div key={card.label} className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                  <p className="text-sm text-slate-400">{card.label}</p>
                  <p className="mt-2 text-3xl font-semibold text-white">{card.value}</p>
                  <p className="mt-1 text-sm text-slate-300">{card.caption}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="grid xl:grid-cols-[1.4fr_1fr] gap-6">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Rooms del equipo</h2>
              <p className="mt-1 text-sm text-slate-500">Elige una sesion activa o revisa trabajo pendiente.</p>
            </div>
          </div>

          <div className="mt-6 grid gap-4">
            {(data?.rooms || []).map((room) => (
              <article key={room.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold text-slate-900">{room.name}</h3>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                        room.status === 'Active'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {room.statusLabel}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-slate-600">{room.summary}</p>
                    <div className="mt-3 flex flex-wrap gap-3 text-sm text-slate-500">
                      <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4" />{room.participantsCount} participantes</span>
                      <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />{room.openTasks} tareas abiertas</span>
                      <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{room.scheduleLabel}</span>
                    </div>
                  </div>

                  <Link to={`/brain-emprendedor/rooms/${room.id}`} className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800">
                    Entrar al room
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-amber-500" />
              <h2 className="text-xl font-semibold text-slate-900">Crear room</h2>
            </div>

            <form onSubmit={createRoom} className="mt-4 space-y-3">
              <input
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-400"
                placeholder="Nombre de la room"
                value={createState.name}
                onChange={(event) => setCreateState((current) => ({ ...current, name: event.target.value }))}
                required
              />
              <textarea
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-400"
                rows={3}
                placeholder="Descripcion breve"
                value={createState.description}
                onChange={(event) => setCreateState((current) => ({ ...current, description: event.target.value }))}
              />
              <textarea
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-400"
                rows={3}
                placeholder="Objetivo de la reunion"
                value={createState.objective}
                onChange={(event) => setCreateState((current) => ({ ...current, objective: event.target.value }))}
              />
              {createState.error && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {createState.error}
                </div>
              )}
              <button
                type="submit"
                disabled={createState.sending}
                className="w-full rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {createState.sending ? 'Creando...' : 'Crear room y abrir flujo'}
              </button>
            </form>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-slate-900">Promesa del MVP</h2>
            <ul className="mt-4 space-y-3 text-sm text-slate-600">
              <li className="rounded-2xl bg-slate-50 p-4">Una sola vista para video, asistentes, notas, tareas y apoyo de IA.</li>
              <li className="rounded-2xl bg-slate-50 p-4">Invitaciones a usuarios autenticados del ecosistema AppEmprendedor.</li>
              <li className="rounded-2xl bg-slate-50 p-4">Feedback final con acuerdos, riesgos, bloqueos y siguientes pasos.</li>
            </ul>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-slate-900">Checklist de implementacion</h2>
            <div className="mt-4 space-y-3">
              {(data?.implementationChecklist || []).map((item) => (
                <div key={item.title} className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 rounded-full p-1.5 ${item.done ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {item.done ? <CheckCircle2 className="w-4 h-4" /> : <Clock3 className="w-4 h-4" />}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{item.title}</p>
                      <p className="mt-1 text-sm text-slate-500">{item.caption}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </section>
    </div>
  )
}
